import type { WorkflowDefinition, WorkflowGraph, WorkflowGraphNode } from '../../api/workflowRuns'
import { workflowStepDefaultName, workflowStepTypeDescription, workflowStepTypeLabel } from './workflowDisplay'
import {
  buildWorkflowTemplateGraph,
  WORKFLOW_TYPE_META,
  WORKFLOW_TYPE_VALUES,
  workflowTypeFromGraph,
  type WorkflowType,
} from './workflowTemplates'

/** 已知系统模板 public_id → 工作流类型（DB 乱码时用于回退展示） */
export const SYSTEM_WORKFLOW_TEMPLATE_TYPES: Record<string, WorkflowType> = {
  '0180c28e-4f84-7f20-91d5-8f2a00101001': 'image_prompt',
  '0180c28e-4f84-7f20-91d5-8f2a00101002': 'custom',
  '0180c28e-4f84-7f20-91d5-8f2a00101003': 'comic_story',
}

export const SYSTEM_WORKFLOW_TEMPLATE_DISPLAY: Record<string, { name: string; description: string }> = {
  '0180c28e-4f84-7f20-91d5-8f2a00101002': {
    name: '流程控制演示工作流',
    description: '演示条件分支与循环体：输入非空走主路径（循环整理→生图），为空走失败结束。',
  },
  '0180c28e-4f84-7f20-91d5-8f2a00101003': {
    name: '漫画项目初始化',
    description: '轻量输入故事方向与约束，AI 生成故事蓝图、章节规划与分页骨架，确认后创建漫画项目。',
  },
}

const SYSTEM_WORKFLOW_INPUT_SCHEMA: Record<string, Record<string, unknown>> = {
  '0180c28e-4f84-7f20-91d5-8f2a00101002': {
    workflowType: 'custom',
    templateVersion: 1,
    fields: [
      {
        key: 'input',
        label: '故事描述',
        type: 'textarea',
        required: false,
        placeholder: '输入非空走主路径；留空走失败分支',
      },
    ],
  },
}

export function isGarbledWorkflowText(text: string | null | undefined): boolean {
  const trimmed = String(text ?? '').trim()
  if (!trimmed) return false
  const questionMarks = (trimmed.match(/\?/g) ?? []).length
  if (questionMarks >= 2 && questionMarks / trimmed.length >= 0.2) return true
  if (trimmed.includes('\uFFFD')) return true
  return /^[\s?？.·…]+$/.test(trimmed)
}

export function resolveWorkflowType(definition: Pick<WorkflowDefinition, 'publicId' | 'graph'>): WorkflowType {
  const fromGraph = workflowTypeFromGraph(definition.graph)
  return SYSTEM_WORKFLOW_TEMPLATE_TYPES[definition.publicId] ?? fromGraph
}

export function resolveWorkflowDefinitionDisplay(definition: WorkflowDefinition) {
  const workflowType = resolveWorkflowType(definition)
  const meta = WORKFLOW_TYPE_META[workflowType]
  const systemDisplay = SYSTEM_WORKFLOW_TEMPLATE_DISPLAY[definition.publicId]
  const name = isGarbledWorkflowText(definition.name)
    ? systemDisplay?.name ?? meta.defaultName
    : definition.name.trim() || systemDisplay?.name || meta.defaultName
  const description = isGarbledWorkflowText(definition.description)
    ? systemDisplay?.description ?? meta.defaultDescription
    : definition.description?.trim() || systemDisplay?.description || meta.defaultDescription
  return {
    workflowType,
    typeLabel: systemDisplay ? '流程控制' : meta.label,
    name,
    description,
  }
}

function templateNodeName(node: WorkflowGraphNode, workflowType: WorkflowType): string | null {
  for (const type of WORKFLOW_TYPE_VALUES) {
    if (workflowType !== 'custom' && type !== workflowType) continue
    const match = buildWorkflowTemplateGraph(type, []).nodes.find(
      (item) => item.nodeId === node.nodeId || item.type === node.type,
    )
    if (match && !isGarbledWorkflowText(match.name)) return match.name
  }
  return null
}

export function resolveWorkflowNodeLabel(node: WorkflowGraphNode, workflowType: WorkflowType): string {
  if (!isGarbledWorkflowText(node.name)) return node.name.trim()
  const fromTemplate = templateNodeName(node, workflowType)
  if (fromTemplate) return fromTemplate
  const defaultName = workflowStepDefaultName(node.type)
  if (defaultName && !isGarbledWorkflowText(defaultName)) return defaultName
  return workflowStepTypeLabel(node.type)
}

export function resolveWorkflowNodeDescription(node: WorkflowGraphNode): string {
  if (!isGarbledWorkflowText(node.description)) return node.description.trim()
  for (const type of WORKFLOW_TYPE_VALUES) {
    const match = buildWorkflowTemplateGraph(type, []).nodes.find((item) => item.nodeId === node.nodeId)
    if (match?.description && !isGarbledWorkflowText(match.description)) return match.description
  }
  return workflowStepTypeDescription(node.type)
}

export function normalizeWorkflowGraphForDisplay(
  graph: WorkflowGraph,
  workflowType?: WorkflowType,
): WorkflowGraph {
  const type = workflowType ?? workflowTypeFromGraph(graph)
  return {
    ...graph,
    nodes: graph.nodes.map((node) => ({
      ...node,
      name: resolveWorkflowNodeLabel(node, type),
      description: resolveWorkflowNodeDescription(node),
    })),
  }
}

export function workflowInputFieldDefaults(
  key: string,
  workflowType?: WorkflowType,
): { label?: string; placeholder?: string } {
  for (const schema of Object.values(SYSTEM_WORKFLOW_INPUT_SCHEMA)) {
    const match = inputSchemaFieldDefaults(key, workflowTypeFromGraph({ inputSchema: schema }), undefined)
    if (match.label || match.placeholder) return match
  }
  if (workflowType) return inputSchemaFieldDefaults(key, workflowType)
  for (const type of WORKFLOW_TYPE_VALUES) {
    const match = inputSchemaFieldDefaults(key, type)
    if (match.label || match.placeholder) return match
  }
  return {}
}

function inputSchemaFieldDefaults(
  key: string,
  workflowType: WorkflowType,
  publicId?: string,
): { label?: string; placeholder?: string } {
  const systemSchema = publicId ? SYSTEM_WORKFLOW_INPUT_SCHEMA[publicId] : undefined
  const typeSchema = buildWorkflowTemplateGraph(workflowType, []).inputSchema
  for (const schema of [systemSchema, typeSchema]) {
    const fields = (schema?.fields ?? []) as Array<Record<string, unknown>>
    const match = fields.find((field) => String(field.key ?? '').trim() === key)
    if (!match) continue
    const label = String(match.label ?? '').trim()
    const placeholder = String(match.placeholder ?? '').trim()
    return {
      label: label && !isGarbledWorkflowText(label) ? label : undefined,
      placeholder: placeholder && !isGarbledWorkflowText(placeholder) ? placeholder : undefined,
    }
  }
  return {}
}

export function normalizeWorkflowInputSchema(
  inputSchema: Record<string, unknown> | null | undefined,
  workflowType?: WorkflowType,
  publicId?: string,
): Record<string, unknown> {
  const schema = { ...(inputSchema ?? {}) }
  const type = workflowType ?? workflowTypeFromGraph({ inputSchema: schema })
  const systemSchema = publicId ? SYSTEM_WORKFLOW_INPUT_SCHEMA[publicId] : undefined
  const rawFields = Array.isArray(schema.fields) ? schema.fields : []
  if (rawFields.length === 0 && systemSchema?.fields) {
    return { ...systemSchema }
  }
  schema.fields = rawFields.map((item) => {
    if (!item || typeof item !== 'object') return item
    const row = { ...(item as Record<string, unknown>) }
    const key = String(row.key ?? '').trim()
    if (!key) return row
    const defaults = inputSchemaFieldDefaults(key, type, publicId)
    const label = String(row.label ?? '').trim()
    const placeholder = String(row.placeholder ?? '').trim()
    if (!label || isGarbledWorkflowText(label)) {
      if (defaults.label) row.label = defaults.label
    }
    if (!placeholder || isGarbledWorkflowText(placeholder)) {
      if (defaults.placeholder) row.placeholder = defaults.placeholder
    }
    return row
  })
  return schema
}

/** 修复图内节点名与输入 schema 中的历史乱码（加载后写回 state，保存可持久化） */
export function repairWorkflowGraphText(
  graph: WorkflowGraph,
  workflowType?: WorkflowType,
  publicId?: string,
): WorkflowGraph {
  const type = workflowType ?? workflowTypeFromGraph(graph)
  const normalized = normalizeWorkflowGraphForDisplay(graph, type)
  return {
    ...normalized,
    inputSchema: normalizeWorkflowInputSchema(graph.inputSchema as Record<string, unknown>, type, publicId),
  }
}
